// src/components/Tareas.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye,
  FiCheckCircle, FiClock, FiX, FiUser, FiCalendar,
  FiAlertCircle, FiRefreshCw, FiSave
} from 'react-icons/fi';
import './Tareas.css';
import './TareasWorkspace.css';
import './TareasCards.css';
import { formatDateTimeUS, formatDateUS } from '../utils/dateFormat';

const Tareas = () => {
  const [tareas, setTareas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Completed work is retained, but the operational inbox opens focused on
  // pending/in-progress tasks. The completed KPI remains the explicit archive.
  const [filterEstado, setFilterEstado] = useState('activas');
  const [filterPrioridad, setFilterPrioridad] = useState('todas');
  const [filterUsuario, setFilterUsuario] = useState('todos');
  const [showModal, setShowModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [editing, setEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  // Keep each desktop page balanced: three columns by four rows.
  const [itemsPerPage] = useState(12);

  const [formData, setFormData] = useState({
    cliente_id: '',
    titulo: '',
    descripcion: '',
    estado: 'pendiente',
    prioridad: 'media',
    fecha_vencimiento: '',
    asignado_a: '',
    asignados_a: [],
    notas: ''
  });

  const API_URL = window.__CRM_API__ || 'http://localhost:8000/api';
  const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
  // SQLite conserva el instante UTC sin el sufijo Z. Lo restauramos como UTC
  // para mostrarlo correctamente en la zona horaria real del navegador.
  const formatCompletion = (value) => {
    if (!value) return '';
    return formatDateTimeUS(value);
  };
  // A YYYY-MM-DD task deadline is a civil date. Parsing it as UTC moves it to
  // the prior day for users in the Americas.
  const formatDueDate = (value) => {
    if (!value) return '';
    return formatDateUS(value, value);
  };
  const isPastDue = (value) => {
    if (!value) return false;
    const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return false;
    const deadline = new Date(year, month - 1, day);
    deadline.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deadline < today;
  };

  const getUsuarioActual = () => {
    const usuario = localStorage.getItem('usuario') || sessionStorage.getItem('usuario');
    if (usuario) {
      try {
        return JSON.parse(usuario);
      } catch {
        return null;
      }
    }
    return null;
  };
  const taskAssignees = (task) => String(task?.asignado_a || '').split('|').map((name) => name.trim()).filter(Boolean);
  const assigneeLabel = (task) => taskAssignees(task).join(' · ') || 'Sin asignar';
  const toggleAssignee = (name) => setFormData((current) => {
    const selected = current.asignados_a || [];
    return {
      ...current,
      asignado_a: '',
      asignados_a: selected.includes(name) ? selected.filter((item) => item !== name) : [...selected, name],
    };
  });

  // ============ CARGAR DATOS - VERSIÓN CORREGIDA ============
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      if (!token) {
        console.warn('⚠️ No hay token. Redirigiendo al login...');
        window.location.href = '/login';
        return;
      }

      console.log('📡 Token encontrado, cargando datos...');

      // Configuración con token
      const config = {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // These independent reads used to run one after another.  On a cold or
      // busy Render instance that made the Tasks module wait for all three
      // round-trips in series. Load them together and retain each fallback.
      const [tareasResult, clientesResult, usuariosResult] = await Promise.allSettled([
        axios.get(`${API_URL}/tareas/`, config),
        axios.get(`${API_URL}/clientes/`, config),
        axios.get(`${API_URL}/auth/usuarios`, config),
      ]);

      if (tareasResult.status === 'fulfilled') {
        setTareas(tareasResult.value.data || []);
      } else {
        const error = tareasResult.reason;
        console.error('❌ Error al cargar tareas:', error.response?.status, error.message);
        if (error.response?.status === 401) {
          alert('⚠️ Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
          window.location.href = '/login';
          return;
        }
        setTareas([]);
      }

      if (clientesResult.status === 'fulfilled') {
        setClientes(clientesResult.value.data || []);
      } else {
        console.error('❌ Error al cargar clientes:', clientesResult.reason);
        setClientes([]);
      }

      if (usuariosResult.status === 'fulfilled') {
        setUsuarios(usuariosResult.value.data || []);
      } else {
        console.warn('⚠️ No se pudieron cargar usuarios, usando mock');
        const usuarioActual = getUsuarioActual();
        const mockUsuarios = [
          { id: 1, nombre: 'Martin Quiroz' },
          { id: 2, nombre: 'Ana Pérez' },
          { id: 3, nombre: 'Carlos Gómez' }
        ];
        if (usuarioActual && !mockUsuarios.some(u => u.nombre === usuarioActual.nombre)) {
          mockUsuarios.unshift({ id: 999, nombre: usuarioActual.nombre });
        }
        setUsuarios(mockUsuarios);
      }

    } catch (error) {
      console.error('❌ Error general en cargarDatos:', error);
      alert('❌ Error al cargar datos. Verifica la consola para más detalles.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar el componente y cada vez que se actualice
  // Carga inicial intencional: la actualización se controla mediante acciones del usuario.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cargarDatos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Función para forzar recarga de clientes (se puede llamar desde el botón)
  const recargarClientes = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const clientesRes = await axios.get(`${API_URL}/clientes/`, config);
      console.log('🔄 Clientes recargados:', clientesRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      console.error('❌ Error al recargar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============ FILTRADO ============
  const tareasFiltradas = tareas.filter(t => {
    const matchesSearch = t.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEstado = filterEstado === 'todas' || (filterEstado === 'activas' ? t.estado !== 'completada' : t.estado === filterEstado);
    const matchesPrioridad = filterPrioridad === 'todas' || t.prioridad === filterPrioridad;
    const matchesUsuario = filterUsuario === 'todos' || taskAssignees(t).includes(filterUsuario);
    return matchesSearch && matchesEstado && matchesPrioridad && matchesUsuario;
  });

  // ============ PAGINACIÓN ============
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTareas = tareasFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(tareasFiltradas.length / itemsPerPage);

  // ============ FUNCIONES AUXILIARES ============
  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellido}` : 'Cliente no encontrado';
  };

  const estadosConfig = {
    pendiente: { label: 'Pendiente', color: '#f39c12', icon: FiClock },
    en_proceso: { label: 'En Proceso', color: '#3498db', icon: FiRefreshCw },
    completada: { label: 'Completada', color: '#27ae60', icon: FiCheckCircle },
    vencida: { label: 'Vencida', color: '#e74c3c', icon: FiAlertCircle }
  };

  const prioridadesConfig = {
    baja: { label: 'Baja', color: '#95a5a6' },
    media: { label: 'Media', color: '#3498db' },
    alta: { label: 'Alta', color: '#f39c12' },
    urgente: { label: 'Urgente', color: '#e74c3c' }
  };

  const getEstadoIcon = (estado) => {
    const Icon = estadosConfig[estado]?.icon || FiClock;
    return <Icon size={14} />;
  };

  // ============ RESET FORMULARIO ============
  const resetForm = () => {
    setFormData({
      cliente_id: '',
      titulo: '',
      descripcion: '',
      estado: 'pendiente',
      prioridad: 'media',
      fecha_vencimiento: '',
      asignado_a: '',
      asignados_a: [],
      notas: ''
    });
    setEditing(false);
  };

  // ============ CRUD - CREAR TAREA ============
  const handleCrearTarea = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    
    if (!formData.titulo.trim()) {
      alert('⚠️ El título es obligatorio');
      return;
    }
    
    if (!formData.cliente_id) {
      alert('⚠️ Debes seleccionar un cliente');
      return;
    }

    setIsSaving(true);
    try {
      const token = getToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const usuario = getUsuarioActual();
      
      const tareaData = {
        cliente_id: parseInt(formData.cliente_id),
        titulo: formData.titulo.trim(),
        descripcion: formData.descripcion || '',
        estado: formData.estado || 'pendiente',
        prioridad: formData.prioridad || 'media',
        fecha_vencimiento: formData.fecha_vencimiento || '',
        asignado_a: formData.asignado_a || '',
        asignados_a: formData.asignados_a || [],
        creado_por: usuario?.nombre || 'Usuario',
        notas: formData.notas || '',
        idempotency_key: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`
      };

      console.log('📤 Enviando tarea:', tareaData);

      const response = await axios.post(`${API_URL}/tareas/`, tareaData, config);
      setTareas([response.data, ...tareas]);
      setShowModal(false);
      resetForm();
      alert('✅ Tarea creada exitosamente');
    } catch (error) {
      console.error('❌ Error al crear tarea:', error);
      alert('❌ Error al crear la tarea. Revisa la consola.');
    } finally {
      setIsSaving(false);
    }
  };

  // ============ CRUD - ACTUALIZAR TAREA ============
  const handleUpdateTarea = async (id, data) => {
    try {
      const token = getToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API_URL}/tareas/${id}`, data, config);
      setTareas(tareas.map(t => t.id === id ? response.data : t));
      if (selectedTarea && selectedTarea.id === id) {
        setSelectedTarea(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Error al actualizar tarea:', error);
      throw error;
    }
  };

  // ============ CRUD - CAMBIAR ESTADO ============
  const handleCambiarEstado = async (id, nuevoEstado) => {
    try {
      await handleUpdateTarea(id, { estado: nuevoEstado });
    } catch (error) {
      alert('❌ Error al cambiar estado');
    }
  };

  // ============ CRUD - ELIMINAR TAREA ============
  const handleEliminarTarea = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta tarea?')) return;
    try {
      const token = getToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/tareas/${id}`, config);
      setTareas(tareas.filter(t => t.id !== id));
      setShowDetalleModal(false);
      alert('✅ Tarea eliminada');
    } catch (error) {
      console.error('Error al eliminar tarea:', error);
      alert('❌ Error al eliminar la tarea');
    }
  };

  // ============ ACCIONES - VER DETALLE ============
  const handleVerDetalle = (tarea) => {
    setSelectedTarea(tarea);
    setShowDetalleModal(true);
  };

  // ============ ACCIONES - EDITAR ============
  const handleEditar = (tarea) => {
    setFormData({
      cliente_id: tarea.cliente_id,
      titulo: tarea.titulo,
      descripcion: tarea.descripcion || '',
      estado: tarea.estado,
      prioridad: tarea.prioridad,
      fecha_vencimiento: tarea.fecha_vencimiento || '',
      asignado_a: tarea.asignado_a || '',
      asignados_a: taskAssignees(tarea),
      notas: tarea.notas || ''
    });
    setEditing(tarea.id);
    setShowModal(true);
  };

  // ============ ACCIONES - GUARDAR EDICIÓN ============
  const handleGuardarEdicion = async (e) => {
    e.preventDefault();
    try {
      await handleUpdateTarea(editing, formData);
      setShowModal(false);
      resetForm();
      alert('✅ Tarea actualizada');
    } catch (error) {
      alert('❌ Error al actualizar la tarea');
    }
  };

  // ============ COMPONENTE TARJETA DE TAREA ============
  const TareaCard = ({ tarea }) => {
    const estadoInfo = estadosConfig[tarea.estado] || estadosConfig.pendiente;
    const prioridadInfo = prioridadesConfig[tarea.prioridad] || prioridadesConfig.media;
    const isVencida = tarea.estado !== 'completada' && isPastDue(tarea.fecha_vencimiento);

    return (
      <div className={`tarea-card ${tarea.estado}`}>
        <div className="tarea-card-header">
          <div className="tarea-titulo">
            <h3>{tarea.titulo}</h3>
            <span className="prioridad-badge" style={{ backgroundColor: prioridadInfo.color + '20', color: prioridadInfo.color }}>
              {prioridadInfo.label}
            </span>
          </div>
          <div className="tarea-acciones-header">
            <button className="btn-detalle" onClick={() => handleVerDetalle(tarea)}>
              <FiEye />
            </button>
            <button className="btn-editar" onClick={() => handleEditar(tarea)}>
              <FiEdit2 />
            </button>
            <button className="btn-eliminar" onClick={() => handleEliminarTarea(tarea.id)}>
              <FiTrash2 />
            </button>
          </div>
        </div>
        <div className="tarea-card-body">
          <p className="tarea-descripcion">{tarea.descripcion || 'Sin descripcion'}</p>
          <div className="tarea-metadata">
            <span className="tarea-cliente"><FiUser size={14} /> {getClienteNombre(tarea.cliente_id)}</span>
            <span className="tarea-asignado"><FiUser size={14} /> {assigneeLabel(tarea)}</span>
          </div>
        </div>
        <div className="tarea-card-footer">
          <div className="tarea-estado">
            <span className="estado-badge" style={{ backgroundColor: estadoInfo.color + '20', color: estadoInfo.color }} onClick={() => {
              const estados = ['pendiente', 'en_proceso', 'completada'];
              const currentIndex = estados.indexOf(tarea.estado);
              const nextIndex = (currentIndex + 1) % estados.length;
              handleCambiarEstado(tarea.id, estados[nextIndex]);
            }}>
              {getEstadoIcon(tarea.estado)} {estadoInfo.label}
            </span>
            {isVencida && <span className="vencida-badge">🔴 Vencida</span>}
          </div>
          {tarea.fecha_vencimiento && (
            <span className="tarea-fecha"><FiCalendar size={14} /> {formatDueDate(tarea.fecha_vencimiento)}</span>
          )}
          {tarea.estado === 'completada' && tarea.completada_en && (
            <span className="tarea-fecha tarea-completada-fecha"><FiCheckCircle size={14} /> Completada {formatCompletion(tarea.completada_en)}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="tareas-module">
      <div className="tareas-header">
        <div className="header-left">
          <h1>✅ Tareas</h1>
          <p>Gestión de tareas y seguimiento</p>
        </div>
        <button className="btn-crear" onClick={() => { resetForm(); setShowModal(true); }}>
          <FiPlus /> Nueva Tarea
        </button>
      </div>

      <div className="tareas-stats">
        <div className="stat-card" onClick={() => setFilterEstado('activas')}>
          <span className="stat-number">{tareas.filter(t => t.estado !== 'completada').length}</span>
          <span className="stat-label">Activas</span>
        </div>
        <div className="stat-card pendiente" onClick={() => setFilterEstado('pendiente')}>
          <span className="stat-number">{tareas.filter(t => t.estado === 'pendiente').length}</span>
          <span className="stat-label">Pendientes</span>
        </div>
        <div className="stat-card en_proceso" onClick={() => setFilterEstado('en_proceso')}>
          <span className="stat-number">{tareas.filter(t => t.estado === 'en_proceso').length}</span>
          <span className="stat-label">En Proceso</span>
        </div>
        <div className="stat-card completada" onClick={() => setFilterEstado('completada')}>
          <span className="stat-number">{tareas.filter(t => t.estado === 'completada').length}</span>
          <span className="stat-label">Completadas</span>
        </div>
        <div className="stat-card vencida" onClick={() => setFilterEstado('vencida')}>
          <span className="stat-number">{tareas.filter(t => t.estado === 'vencida').length}</span>
          <span className="stat-label">Vencidas</span>
        </div>
      </div>

      <div className="tareas-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input type="text" placeholder="Buscar tareas..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterPrioridad} onChange={(e) => setFilterPrioridad(e.target.value)}>
            <option value="todas">Todas las prioridades</option>
            <option value="baja">Baja</option>
            <option value="media">Media</option>
            <option value="alta">Alta</option>
            <option value="urgente">Urgente</option>
          </select>
          <select className="filter-select" value={filterUsuario} onChange={(e) => setFilterUsuario(e.target.value)}>
            <option value="todos">Todos los usuarios</option>
            {usuarios.map(u => <option key={u.id} value={u.nombre}>{u.nombre}</option>)}
          </select>
          <button className="btn-refresh" onClick={cargarDatos}><FiRefreshCw /></button>
          <button className="btn-refresh" onClick={recargarClientes} style={{ background: '#e8f4fd', marginLeft: '4px' }} title="Recargar clientes">🔄 Clientes</button>
        </div>
      </div>

      <div className="tareas-grid">
        {loading ? (
          <div className="loading-container"><div className="loader"></div><p>Cargando tareas...</p></div>
        ) : currentTareas.length === 0 ? (
          <div className="empty-state"><FiCheckCircle className="empty-icon" /><h3>No hay tareas</h3><p>Crea tu primera tarea usando el botón "Nueva Tarea"</p></div>
        ) : (
          currentTareas.map(tarea => <TareaCard key={tarea.id} tarea={tarea} />)
        )}
      </div>

      {tareasFiltradas.length > itemsPerPage && (
        <div className="pagination">
          <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Anterior</button>
          <span className="page-info">Página {currentPage} de {totalPages}</span>
          <button className="page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Siguiente</button>
        </div>
      )}

      {/* MODAL CREAR/EDITAR */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-tarea" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? '✏️ Editar Tarea' : '📝 Nueva Tarea'}</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}><FiX /></button>
            </div>
            <div className="modal-body">
              <form onSubmit={editing ? handleGuardarEdicion : handleCrearTarea} className="tarea-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Título *</label>
                    <input type="text" name="titulo" value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} placeholder="Título de la tarea" required />
                  </div>
                  <div className="form-group">
                    <label><FiUser /> Cliente *</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        name="cliente_id"
                        value={formData.cliente_id}
                        onChange={(e) => setFormData({...formData, cliente_id: e.target.value})}
                        required
                        style={{ flex: 1 }}
                      >
                        <option value="">-- Seleccionar cliente --</option>
                        {clientes.length === 0 ? (
                          <option value="" disabled>⚠️ No hay clientes disponibles</option>
                        ) : (
                          clientes.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nombre} {c.apellido}
                            </option>
                          ))
                        )}
                      </select>
                      <button 
                        type="button" 
                        onClick={recargarClientes} 
                        style={{ 
                          padding: '10px 14px', 
                          background: '#e8f4fd', 
                          border: 'none', 
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '18px'
                        }}
                        title="Recargar clientes"
                      >
                        🔄
                      </button>
                    </div>
                    {clientes.length === 0 && (
                      <small style={{ color: '#e74c3c' }}>
                        ⚠️ No hay clientes. <a href="/clientes" style={{ color: '#4a8ac4' }}>Ir a Clientes</a> para crear uno.
                      </small>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prioridad</label>
                    <select name="prioridad" value={formData.prioridad} onChange={(e) => setFormData({...formData, prioridad: e.target.value})}>
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Asignar a</label>
                    <div className="assignee-checkbox-list" role="group" aria-label="Usuarios asignados">
                      {usuarios.map((user) => <label key={user.id} className="assignee-checkbox"><input type="checkbox" checked={(formData.asignados_a || []).includes(user.nombre)} onChange={() => toggleAssignee(user.nombre)} /><span>{user.nombre}</span></label>)}
                    </div>
                    <small>Marque a todos los responsables. Cada uno recibe su correo y recordatorios.</small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fecha de vencimiento</label>
                    <input type="date" name="fecha_vencimiento" value={formData.fecha_vencimiento} onChange={(e) => setFormData({...formData, fecha_vencimiento: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Estado</label>
                    <select name="estado" value={formData.estado} onChange={(e) => setFormData({...formData, estado: e.target.value})}>
                      <option value="pendiente">Pendiente</option>
                      <option value="en_proceso">En Proceso</option>
                      <option value="completada">Completada</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Descripcion</label>
                  <textarea name="descripcion" value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} rows="3" placeholder="Descripcion detallada..." />
                </div>
                <div className="form-group">
                  <label>Notas</label>
                  <textarea name="notas" value={formData.notas} onChange={(e) => setFormData({...formData, notas: e.target.value})} rows="2" placeholder="Notas adicionales..." />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</button>
                  <button type="submit" className="btn-save" disabled={isSaving}><FiSave /> {isSaving ? 'Guardando…' : editing ? 'Guardar Cambios' : 'Crear Tarea'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALLE */}
      {showDetalleModal && selectedTarea && (
        <div className="modal-overlay" onClick={() => setShowDetalleModal(false)}>
          <div className="modal-detalle" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 {selectedTarea.titulo}</h2>
              <button className="modal-close" onClick={() => setShowDetalleModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="detalle-grid">
                <div className="detalle-item"><span className="detalle-label">Estado</span><span className="detalle-value"><span className="estado-badge" style={{ backgroundColor: estadosConfig[selectedTarea.estado]?.color + '20', color: estadosConfig[selectedTarea.estado]?.color }}>{getEstadoIcon(selectedTarea.estado)} {estadosConfig[selectedTarea.estado]?.label}</span></span></div>
                <div className="detalle-item"><span className="detalle-label">Prioridad</span><span className="detalle-value"><span className="prioridad-badge" style={{ backgroundColor: prioridadesConfig[selectedTarea.prioridad]?.color + '20', color: prioridadesConfig[selectedTarea.prioridad]?.color }}>{prioridadesConfig[selectedTarea.prioridad]?.label}</span></span></div>
                <div className="detalle-item"><span className="detalle-label">Cliente</span><span className="detalle-value">{getClienteNombre(selectedTarea.cliente_id)}</span></div>
                <div className="detalle-item"><span className="detalle-label">Asignado a</span><span className="detalle-value">{assigneeLabel(selectedTarea)}</span></div>
                <div className="detalle-item"><span className="detalle-label">Fecha Vencimiento</span><span className="detalle-value">{selectedTarea.fecha_vencimiento ? formatDueDate(selectedTarea.fecha_vencimiento) : 'Sin fecha'}</span></div>
                <div className="detalle-item"><span className="detalle-label">Creado por</span><span className="detalle-value">{selectedTarea.creado_por || 'Sistema'}</span></div>
                {selectedTarea.fecha_creacion && <div className="detalle-item"><span className="detalle-label">Creada</span><span className="detalle-value">{formatCompletion(selectedTarea.fecha_creacion)}</span></div>}
                {selectedTarea.completada_en && <div className="detalle-item"><span className="detalle-label">Completada</span><span className="detalle-value">{formatCompletion(selectedTarea.completada_en)}{selectedTarea.completada_por ? ` · ${selectedTarea.completada_por}` : ''}</span></div>}
              </div>
              {selectedTarea.descripcion && <div className="detalle-seccion"><h4>Descripcion</h4><p>{selectedTarea.descripcion}</p></div>}
              {selectedTarea.notas && <div className="detalle-seccion"><h4>Notas</h4><p>{selectedTarea.notas}</p></div>}
              <div className="detalle-acciones">
                <button className="btn-completar" onClick={() => { handleCambiarEstado(selectedTarea.id, 'completada'); setShowDetalleModal(false); }} disabled={selectedTarea.estado === 'completada'}><FiCheckCircle /> Completar</button>
                <button className="btn-editar-detalle" onClick={() => { setShowDetalleModal(false); handleEditar(selectedTarea); }}><FiEdit2 /> Editar</button>
                <button className="btn-eliminar-detalle" onClick={() => handleEliminarTarea(selectedTarea.id)}><FiTrash2 /> Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tareas;
